import { Button } from '@HoudiniUI/Button';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@HoudiniUI/Select';
import {
	buildRegEdSubmissionMetadata,
	IRegEdSubmissionMetadata,
	useRegEdMetadataSchemaQuery,
	useRegEdSupportedFormatsQuery,
} from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { RegEdMetadataForm } from '../../../compliance/RegEdMetadataForm';
import {
	findMissingRequiredFieldIds,
	stripHiddenFieldAnswers,
} from '../../../compliance/RegEdMetadataForm/regEdMetadataFormAdapter';
import { styleSheet } from './styles';

interface IProps {
	impersonationContext?: Api.IImpersonationContext;
	disabled?: boolean;
	isLoading?: boolean;
	/** Seed the format/answers when re-submitting a revision, so the advisor edits the prior filing. */
	initialFormat?: string;
	initialAnswers?: Record<string, unknown>;
	onScheduleRegEdCompliance(e: React.MouseEvent<HTMLElement>, metadata: IRegEdSubmissionMetadata): void;
	styles?: StyleDeclarationValue[];
}

/**
 * Hands the metadata payload back to the caller rather than submitting — the submissions POST can
 * only happen once the parent (and its server-built approval request) exists.
 */
export const RegEdComplianceApprovalPrompt: React.FC<IProps> = ({
	impersonationContext,
	disabled,
	isLoading,
	initialFormat,
	initialAnswers,
	onScheduleRegEdCompliance,
	styles = [],
}) => {
	const [format, setFormat] = React.useState(initialFormat ?? '');
	const [answers, setAnswers] = React.useState<Record<string, unknown>>(initialAnswers ?? {});

	// The parent may resolve format and answers on different renders; latch each prop separately so
	// a late-arriving answers bag still seeds, and edits are never clobbered.
	const seededFormat = React.useRef(false);
	const seededAnswers = React.useRef(false);
	React.useEffect(() => {
		if (!seededFormat.current && initialFormat !== undefined) {
			setFormat(initialFormat);
			seededFormat.current = true;
		}
		if (!seededAnswers.current && initialAnswers !== undefined) {
			setAnswers(initialAnswers);
			seededAnswers.current = true;
		}
	}, [initialFormat, initialAnswers]);

	const { data: supportedFormats, isLoading: formatsLoading } = useRegEdSupportedFormatsQuery({ impersonationContext });
	const formats = supportedFormats ?? [];

	const { data: schema, isLoading: schemaLoading } = useRegEdMetadataSchemaQuery(format || null, {
		impersonationContext,
	});

	const handleFormatChange = (next: string) => {
		setFormat(next);
		setAnswers({});
	};

	const handleSubmit = (e: React.MouseEvent<HTMLElement>) => {
		if (!format) {
			toast.error('Please choose a submission format.');
			return;
		}
		const missing = findMissingRequiredFieldIds(schema, answers);
		if (missing.length > 0) {
			toast.error(`Please complete all required fields (${missing.length} remaining).`);
			return;
		}
		onScheduleRegEdCompliance(e, buildRegEdSubmissionMetadata(format, stripHiddenFieldAnswers(schema, answers)));
	};

	return (
		<div className={css(styleSheet.container, ...styles)}>
			<div className={css(styleSheet.title)}>Submit for compliance review</div>
			<div className={css(styleSheet.detail)}>
				Choose the submission format and complete the compliance questionnaire. This is sent to your firm's compliance
				system for review.
			</div>

			<div className={css(styleSheet.field)}>
				<span className={css(styleSheet.label)}>Format</span>
				<Select
					value={format}
					onValueChange={handleFormatChange}
					disabled={disabled || isLoading || formatsLoading || formats.length === 0}
				>
					<SelectTrigger styles={styleSheet.selectTrigger}>
						<span>{format || (formatsLoading ? 'Loading formats…' : 'Select a format…')}</span>
					</SelectTrigger>
					<SelectContent>
						{formats.map(f => (
							<SelectItem key={f} value={f}>
								{f}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				{!formatsLoading && formats.length === 0 ? (
					<span className={css(styleSheet.detail)}>No submission formats are configured for your firm.</span>
				) : null}
			</div>

			{format && schemaLoading ? <div className={css(styleSheet.detail)}>Loading questionnaire…</div> : null}
			{format && !schemaLoading && !schema ? (
				<div className={css(styleSheet.detail)}>No compliance questionnaire is configured for this format.</div>
			) : null}
			{schema ? (
				<RegEdMetadataForm schema={schema} answers={answers} onChange={setAnswers} disabled={disabled || isLoading} />
			) : null}

			<Button disabled={disabled || isLoading || !schema} isLoading={isLoading} onClick={handleSubmit}>
				Schedule for Compliance Review
			</Button>
		</div>
	);
};
